import { DropPosition, moveFieldInDraft, OrganizeFieldsDraft } from "@utils/organizeFields";
import { isInsideRect, workspaceDropPosition } from "@utils/workspacesOrder";
import { useRef, useState } from "react";

// Drag type of a field of the "Organize fields" dialog
export const FIELD_DRAG_TYPE = "application/x-compozz-field";

export interface FieldDropTarget {
    // Field the dragged one is dropped next to
    key: string;
    position: DropPosition;
}

function sameTarget(a: FieldDropTarget | null, b: FieldDropTarget | null): boolean {
    return a?.key === b?.key && a?.position === b?.position;
}

interface UseOrganizeFieldsDragParams {
    draft: OrganizeFieldsDraft;
    // Fields which cannot move: they can be neither dragged nor dropped on
    pinned: ReadonlySet<string>;
    enabled: boolean;
    // Last field which can move: dropping below the last row means after it
    lastMovableKey: string | null;
    // Called with the draft once a field is dropped somewhere else
    onMove: (draft: OrganizeFieldsDraft) => void;
}

/**
 * Drag & drop of the fields of the "Organize fields" dialog, to change their
 * order in the draft (nothing is saved before the dialog is validated). A field
 * is dragged by its row (handle, name...) and dropped before or after another
 * one, depending on the half of the row the pointer is on.
 * The dialog is portaled: its drag events would bubble through the React tree
 * to the components which render it, they stop at the dialog.
 */
export function useOrganizeFieldsDrag({ draft, pinned, enabled, lastMovableKey, onMove }: UseOrganizeFieldsDragParams) {
    const [draggedKey, setDraggedKey] = useState<string | null>(null);
    const [dropTarget, setDropTarget] = useState<FieldDropTarget | null>(null);
    // The handlers read the refs, the states are only there to render: a drop
    // may follow a drag-over before React has rendered the state it set
    const draggedKeyRef = useRef<string | null>(null);
    const dropTargetRef = useRef<FieldDropTarget | null>(null);

    const updateDropTarget = (target: FieldDropTarget | null) => {
        if (!sameTarget(dropTargetRef.current, target)) {
            dropTargetRef.current = target;
            setDropTarget(target);
        }
    };

    const endDrag = () => {
        draggedKeyRef.current = null;
        setDraggedKey(null);
        updateDropTarget(null);
    };

    // Accepts the drop on the given target, unless it changes nothing: the
    // drop is then refused (no drop line, not-allowed cursor)
    const offerDropTarget = (e: React.DragEvent<HTMLElement>, target: FieldDropTarget) => {
        const dragged = draggedKeyRef.current;
        if (!dragged || !moveFieldInDraft(draft, dragged, target.key, target.position, pinned)) {
            updateDropTarget(null);
            return;
        }
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        updateDropTarget(target);
    };

    const handleDragStart = (e: React.DragEvent<HTMLElement>, key: string) => {
        e.stopPropagation();
        if (!enabled || pinned.has(key)) {
            e.preventDefault();
            return;
        }
        e.dataTransfer.setData(FIELD_DRAG_TYPE, key);
        e.dataTransfer.effectAllowed = "move";
        draggedKeyRef.current = key;
        setDraggedKey(key);
    };

    const handleRowDragOver = (e: React.DragEvent<HTMLElement>, key: string) => {
        if (!draggedKeyRef.current) {
            return;
        }
        e.stopPropagation();
        const position = workspaceDropPosition(e.currentTarget.getBoundingClientRect(), e.clientX, e.clientY, true);
        offerDropTarget(e, { key, position });
    };

    // Drag-over of the list itself, or of a row which cannot move
    const handleListDragOver = (e: React.DragEvent<HTMLElement>) => {
        if (!draggedKeyRef.current) {
            return;
        }
        e.stopPropagation();
        const lastRow = e.currentTarget.lastElementChild?.getBoundingClientRect();
        if (lastMovableKey && lastRow && e.clientY > lastRow.top + lastRow.height) {
            // Empty space below the last row: the end of the list
            offerDropTarget(e, { key: lastMovableKey, position: 'after' });
        } else if (dropTargetRef.current) {
            // Between two rows, or on a row which cannot move: the last target is kept
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
        }
    };

    const handleListDragLeave = (e: React.DragEvent<HTMLElement>) => {
        e.stopPropagation();
        // Leaving a row for another one also bubbles here: the target is only
        // forgotten when the pointer leaves the list
        if (!isInsideRect(e.currentTarget.getBoundingClientRect(), e.clientX, e.clientY)) {
            updateDropTarget(null);
        }
    };

    const handleListDrop = (e: React.DragEvent<HTMLElement>) => {
        const dragged = draggedKeyRef.current;
        const target = dropTargetRef.current;
        if (!dragged) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        endDrag();
        const moved = target ? moveFieldInDraft(draft, dragged, target.key, target.position, pinned) : null;
        if (moved) {
            onMove(moved);
        }
    };

    const listDragProps = {
        onDragOver: handleListDragOver,
        onDragLeave: handleListDragLeave,
        onDrop: handleListDrop,
    };

    // Props of the row of a field which can move: it is dragged, and the other
    // fields are dropped on it
    const rowDragProps = (key: string) => ({
        draggable: enabled,
        onDragStart: (e: React.DragEvent<HTMLElement>) => handleDragStart(e, key),
        onDragEnd: endDrag,
        onDragOver: (e: React.DragEvent<HTMLElement>) => handleRowDragOver(e, key),
    });

    return { draggedKey, dropTarget, listDragProps, rowDragProps };
}
